import { NavLink, Outlet } from "react-router-dom";
import { Typography } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { ClientScopeProvider } from "@/components/pages/Client/settings/ClientScopeContext";
import { isAssistantAccountType } from "@/constants/accountTypes";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { clsx } from "@/utils";
import { useManageClient } from "../context";

const baseSettingsLinks = [
  { name: "Client Profile", to: "profile" },
  { name: "Email Notifications", to: "notifications" },
  { name: "Payment Settings", to: "payments" },
  { name: "Assistants", to: "assistants" },
  { name: "Admin Settings", to: "admin-settings" },
];

/**
 * Settings tab of the operator's Manage-Client record. Renders the same
 * sub-navigation as the client portal's `ClientSettings`, but scopes every
 * reused settings screen to the managed client via `ClientScopeProvider`.
 */
export const ManageClientSettings = () => {
  const { clientId, isViewOnly } = useManageClient();
  // An EA can't have an assistant of their own, so hide "Assistants" when the managed account is an
  // EA. Reuses the parent's RTK-cached profile query — no extra request.
  const { data: profile } = useGetClientProfileQuery({ accountId: clientId });
  const isAssistant = isAssistantAccountType(profile?.account_type);
  const settingsLinks = baseSettingsLinks.filter((link) => link.to !== "assistants" || !isAssistant);

  return (
    <ClientScopeProvider accountId={clientId} isViewOnly={isViewOnly}>
      {/* Full-bleed surface: -mx cancels the content wrapper's px so the gray reaches edge to edge. */}
      <div className="flex flex-1 flex-col">
        <div className="px-3 pb-12 pt-4 lg:px-5">
          <Typography variant="h2" className="mb-3 block">
            Settings
          </Typography>

          <div className="mb-3 lg:hidden">
            <Tabs tabs={settingsLinks} mobileVariant="sheet" />
          </div>

          <div className="rounded-lg border border-neutral-mid-gray bg-white lg:flex">
            <nav className="hidden shrink-0 flex-col border-r border-neutral-gray p-5 lg:flex" aria-label="Client settings">
              {settingsLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    clsx("rounded-lg px-4 py-3 transition-colors", {
                      "bg-primary-light": isActive,
                      "hover:bg-neutral-surface-gray": !isActive,
                    })
                  }
                >
                  {({ isActive }) => (
                    <Typography
                      variant={isActive ? "action" : "paragraph"}
                      className={clsx("!m-0 whitespace-nowrap", isActive ? "text-primary" : "text-neutral-black")}
                    >
                      {link.name}
                    </Typography>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Removed clients are view-only (DEV-1177): disable all form controls in the reused settings panels. */}
            <fieldset disabled={isViewOnly} className="min-w-0 flex-1 p-5">
              <Outlet />
            </fieldset>
          </div>
        </div>
      </div>
    </ClientScopeProvider>
  );
};
